import React from 'react';
import * as THREE from 'three';
import { useThree, invalidate } from '@react-three/fiber';
import {
  Billboard, Line, Text, TransformControls
} from '@react-three/drei';
import { Mesh as ThreeMesh } from 'three/src/objects/Mesh';
import { Color } from 'three';
import {
  BeachSurface,
  buildPourColors,
  defaultFillAnchor,
  distinctColors,
  FillAnchor,
  meshVolume,
  nextSliceBase,
  resolveNewPourBeachAngle,
  sortPoursChronologically,
  StopeFillWalk
} from 'modules/project/StopeFillPage/Three/pour';
import { buildSliceSolid, buildUnfilledSolid } from 'modules/project/StopeFillPage/Three/fillSolids';
import { walkStopeFillOffThread } from 'modules/project/StopeFillPage/Three/walkStopeFillOffThread';
import { orientedFootprint, OrientedFootprint } from 'modules/project/StopeFillPage/Three/orientedFootprint';
import { usePoursQuery } from 'providers/api';
import { useScenario } from 'modules/project/providers/scenario';
import { MODEL_DRAW_WIDTH } from 'config';
import {
  getStopeFillSceneState,
  PourVolumeInfo,
  StopeDimensions,
  subscribeToStopeFillSceneState,
  updateStopeFillSceneState
} from '../../Scenes/StopeFillScene/stopeFillSceneState';
import { StopeFillState } from '../types';
import { focusCameraOnPoint } from '../cameraFraming';
import useStopeGeometry from './useStopeGeometry';

const CLOSED_DRAWER_RAIL_WIDTH = 80;

interface StopeFillContentProps {
  stopeFillState: StopeFillState;
}

interface PourHeightLabel {
  id: string;
  midY: number;
  height: number;
}

// The shell and the pour it belongs to are carried together. The shell is a fresh CSG Brush, so its
// Object3D uuid is a random one with no relation to the pour - keying colour or selection off it silently
// matches nothing and leaves the shell with whatever material the boolean happened to give it.
interface PourModel {
  entityId: string;
  mesh: ThreeMesh;
}

interface StopeAnnotations {
  dimensions: StopeDimensions;
  boxMin: [number, number, number];
  boxMax: [number, number, number];
  // The horizontal frame the L and W lines are drawn in. Only Y still comes from the box.
  footprint: OrientedFootprint;
  pours: PourHeightLabel[];
  fontSize: number;
}

type Point3 = [number, number, number];

interface DimensionSpec {
  key: string;
  start: Point3;
  end: Point3;
  label: string;
  color: string;
}

const STOPE_SHELL_EDGE_THRESHOLD_ANGLE = 30;
const UP_AXIS = new THREE.Vector3(0, 1, 0);
const DIMENSION_COLOR = '#ffffff';
const POUR_DIMENSION_COLOR = '#ffffff';
const DIMENSION_OUTLINE_COLOR = '#1d2733';

const formatMetres = (value: number): string => `${value.toFixed(1)} m`;

// Captures the stope bounding box (geometry frame) and per-pour heights. The actual dimension lines are
// derived from the box at render time by buildDimensionSpecs.
const buildStopeAnnotations = (
  box: THREE.Box3,
  dimensions: StopeDimensions,
  footprint: OrientedFootprint,
  pours: Array<{ id: string; midY: number; height: number }>,
): StopeAnnotations => {
  const maxDim = Math.max(dimensions.length, dimensions.width, dimensions.height, 1);

  return {
    dimensions,
    boxMin: [box.min.x, box.min.y, box.min.z],
    boxMax: [box.max.x, box.max.y, box.max.z],
    footprint,
    pours: pours.map((pour) => ({ id: pour.id, midY: pour.midY, height: pour.height })),
    fontSize: maxDim * 0.03,
  };
};

// Lays out a dimension line per stope axis (L along strike, W across it, H along Y) just outside the
// footprint rectangle, plus a vertical dimension line for each pour band further out past the L end.
//
// The rectangle is the shape's own, not the world grid's, so these lines run at whatever bearing the
// stope lies at. Only H still comes off the bounding box, since no rotation about Y changes it.
const buildDimensionSpecs = (annotations: StopeAnnotations): { fontSize: number; specs: DimensionSpec[] } => {
  const [, minY] = annotations.boxMin;
  const [, maxY] = annotations.boxMax;
  const f = annotations.fontSize;
  const pad = f * 1.5;
  const { length, width, height } = annotations.dimensions;
  const {
    lengthAxis, widthAxis, centreX, centreZ,
  } = annotations.footprint;
  const halfLength = length / 2;
  const halfWidth = width / 2;

  // A point `along` metres along strike and `across` metres across it, measured from the
  // footprint centre, at height `y`.
  const at = (along: number, across: number, y: number): Point3 => [
    centreX + lengthAxis.x * along + widthAxis.x * across,
    y,
    centreZ + lengthAxis.z * along + widthAxis.z * across,
  ];

  const specs: DimensionSpec[] = [
    {
      key: 'L',
      start: at(-halfLength, halfWidth + pad, minY - pad),
      end: at(halfLength, halfWidth + pad, minY - pad),
      label: `L ${formatMetres(length)}`,
      color: DIMENSION_COLOR,
    },
    {
      key: 'W',
      start: at(halfLength + pad, -halfWidth, minY - pad),
      end: at(halfLength + pad, halfWidth, minY - pad),
      label: `W ${formatMetres(width)}`,
      color: DIMENSION_COLOR,
    },
    {
      key: 'H',
      start: at(halfLength + pad, halfWidth + pad, minY),
      end: at(halfLength + pad, halfWidth + pad, maxY),
      label: `H ${formatMetres(height)}`,
      color: DIMENSION_COLOR,
    },
    ...annotations.pours.map((pour) => ({
      key: `pour-${pour.id}`,
      start: at(halfLength + f * 6, 0, pour.midY - pour.height / 2),
      end: at(halfLength + f * 6, 0, pour.midY + pour.height / 2),
      label: formatMetres(pour.height),
      color: POUR_DIMENSION_COLOR,
    })),
  ];

  return { fontSize: f, specs };
};

// Draws a single dimension line: a thin shaft with an arrowhead at each end and a billboarded label at
// the midpoint, all in the stope geometry frame so it scales with the model display group.
const AxisDimension = ({ start, end, label, fontSize, color }: {
  start: Point3;
  end: Point3;
  label: string;
  fontSize: number;
  color: string;
}) => {
  const startVec = new THREE.Vector3(...start);
  const endVec = new THREE.Vector3(...end);
  const direction = endVec.clone().sub(startVec);
  const length = direction.length() || 1;
  const unit = direction.clone().divideScalar(length);
  const mid = startVec.clone().add(endVec).multiplyScalar(0.5);
  const headLength = Math.min(fontSize * 1.6, length * 0.4);
  const headRadius = fontSize * 0.45;
  const endQuat = new THREE.Quaternion().setFromUnitVectors(UP_AXIS, unit);
  const startQuat = new THREE.Quaternion().setFromUnitVectors(UP_AXIS, unit.clone().negate());
  const endConePos = endVec.clone().addScaledVector(unit, -headLength / 2);
  const startConePos = startVec.clone().addScaledVector(unit, headLength / 2);

  return (
    <>
      <Line points={[startVec, endVec]} color={color} lineWidth={1.5} />
      <mesh position={endConePos} quaternion={endQuat}>
        <coneGeometry args={[headRadius, headLength, 12]} />
        <meshBasicMaterial color={color} />
      </mesh>
      <mesh position={startConePos} quaternion={startQuat}>
        <coneGeometry args={[headRadius, headLength, 12]} />
        <meshBasicMaterial color={color} />
      </mesh>
      <Billboard position={[mid.x, mid.y, mid.z]}>
        <Text
          fontSize={fontSize}
          color={color}
          anchorX="center"
          anchorY="middle"
          outlineWidth={fontSize * 0.06}
          outlineColor={DIMENSION_OUTLINE_COLOR}
        >
          {label}
        </Text>
      </Billboard>
    </>
  );
};

const StopeGeometryShell = ({ mesh }: { mesh: ThreeMesh }) => {
  const edgesGeometry = React.useMemo(
    () => new THREE.EdgesGeometry(mesh.geometry, STOPE_SHELL_EDGE_THRESHOLD_ANGLE),
    [mesh.geometry],
  );

  React.useEffect(() => () => {
    edgesGeometry.dispose();
  }, [edgesGeometry]);

  return (
    <>
      <primitive object={mesh} />
      <lineSegments geometry={edgesGeometry} raycast={() => { }}>
        <lineBasicMaterial
          attach="material"
          color="#9fbdd6"
          transparent
          opacity={0.6}
          depthTest={false}
          depthWrite={false}
        />
      </lineSegments>
    </>
  );
};

const safeCalculateMeshVolume = (mesh: ThreeMesh | undefined | null): number => {
  if (!mesh) return 0;

  try {
    return meshVolume(mesh);
  } catch {
    return 0;
  }
};

const StopeFillContent = ({ stopeFillState }: StopeFillContentProps) => {
  const { projectId, stopeId, geometryPath, drawerOpen, projectBeachAngleDegrees } = stopeFillState;

  const {
    camera, controls: threeControls, size: canvasSize, viewport,
  } = useThree();
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);

  const sceneState = getStopeFillSceneState();
  const { enablePositionMove, selectedPourId, pourColorDisplay, previewPour } = sceneState;

  const [stopeModel, setStopeModel] = React.useState<ThreeMesh>();
  const [remainingStopeModel, setRemainingStopeModel] = React.useState<ThreeMesh>();
  const [remainingStopeAfterPreview, setRemainingStopeAfterPreview] = React.useState<ThreeMesh | null>(null);
  const [pourModels, setPourModels] = React.useState<PourModel[]>([]);
  const [previewPourModel, setPreviewPourModel] = React.useState<ThreeMesh | null>(null);
  const [poursColor, setPoursColor] = React.useState<Map<string, Color>>(new Map());
  const [arrowPosition, setArrowPosition] = React.useState<THREE.Vector3>(new THREE.Vector3(0, 10, 0));
  const [modelScale, setModelScale] = React.useState<number>(1);
  const [modelOffset, setModelOffset] = React.useState<THREE.Vector3>(new THREE.Vector3());
  const [defaultAnchor, setDefaultAnchor] = React.useState<FillAnchor>({ x: 0, z: 0 });
  const [annotations, setAnnotations] = React.useState<StopeAnnotations | null>(null);

  const stopeBoxRef = React.useRef<THREE.Box3 | null>(null);
  const stopeFootprintRef = React.useRef<OrientedFootprint | null>(null);
  const stopeDimensionsRef = React.useRef<StopeDimensions | null>(null);
  const processedStopeRef = React.useRef<ThreeMesh | null>(null);
  const stopeModelPathRef = React.useRef<string | null>(null);
  const originalRemainingStopeModelRef = React.useRef<ThreeMesh>();
  const remainingStopeModelRef = React.useRef<ThreeMesh>();
  const walkRef = React.useRef<StopeFillWalk | null>(null);
  const framedGeometryPathRef = React.useRef<string | null>(null);
  const baseViewSizeRef = React.useRef<number | null>(null);

  const { mesh: loadedStope, sourceBounds: loadedSourceBounds, path: loadedGeometryPath } = useStopeGeometry(geometryPath);

  // Published so exports can map local-frame pour solids back to mine-grid coordinates.
  React.useEffect(() => {
    updateStopeFillSceneState({ stopeSourceBounds: loadedSourceBounds });
  }, [loadedSourceBounds]);

  const arrowGroupRef = React.useRef<THREE.Group>(null);

  const { activeScenarioId } = useScenario();
  const { data: poursData, isError: poursFailed } = usePoursQuery(
    { projectId, stopeId, scenarioId: activeScenarioId ?? undefined, includeRockFill: true },
    { enabled: !!projectId && !!stopeId, staleTime: 120000000 },
  );

  const handleArrowChange = React.useCallback(() => {
    if (arrowGroupRef.current && modelScale !== 0) {
      const pos = arrowGroupRef.current.position;
      setArrowPosition(pos.clone());
      const localX = (pos.x / modelScale) - modelOffset.x;
      const localZ = (pos.z / modelScale) - modelOffset.z;

      // No probe here. Moving the pipe moves the anchor, and the datum the next slice starts from is
      // re-read as the fill surface under the new position - see dischargeDatum.
      updateStopeFillSceneState({ pipePosition: { x: localX, y: localZ } });
      invalidate();
    }
  }, [modelScale, modelOffset]);

  const pointerDownPosRef = React.useRef<{ x: number; y: number } | null>(null);

  const handlePourPointerDown = React.useCallback((event: { clientX: number; clientY: number }) => {
    pointerDownPosRef.current = { x: event.clientX, y: event.clientY };
  }, []);

  // Only treat a pointer-up as a selection if the pointer barely moved since pointer-down. A rotate
  // (click-drag-release) over a pour would otherwise register as a click and select it on release.
  const handlePourClick = React.useCallback((pourId: string, event: { clientX: number; clientY: number }) => {
    const down = pointerDownPosRef.current;
    if (down && Math.hypot(event.clientX - down.x, event.clientY - down.y) > 5) {
      return;
    }
    updateStopeFillSceneState({ selectedPourId: pourId });
  }, []);

  const groupPosition = React.useMemo(
    () => modelOffset.clone().multiplyScalar(modelScale),
    [modelOffset, modelScale],
  );

  React.useEffect(() => {
    const unsubscribe = subscribeToStopeFillSceneState(() => {
      forceUpdate();
      invalidate();
    });
    return unsubscribe;
  }, []);

  React.useEffect(() => {
    if (!loadedStope) return;
    if (processedStopeRef.current === loadedStope) return;
    processedStopeRef.current = loadedStope;
    stopeModelPathRef.current = loadedGeometryPath;

    const stope = loadedStope;

    stope.geometry.computeBoundingBox();
    const box = new THREE.Box3().setFromObject(stope);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDimension = Math.max(size.x, size.y, size.z);

    const targetMaxDimension = 100;
    const scale = maxDimension > targetMaxDimension ? targetMaxDimension / maxDimension : 1;
    const offset = center.clone().negate();

    setModelScale(scale);
    setModelOffset(offset);
    setDefaultAnchor(defaultFillAnchor(stope));

    stope.geometry.computeBoundingBox();
    stopeBoxRef.current = stope.geometry.boundingBox?.clone() ?? null;
    // L and W come from the tightest rectangle around the horizontal outline, not from the world-axis
    // box: a stope lying diagonal to the grid has its own diagonal measured in both directions
    // otherwise, which reads as far wider than it is. Height is unaffected by any rotation about Y.
    const footprint = orientedFootprint(stope.geometry);
    stopeFootprintRef.current = footprint;
    const dimensions: StopeDimensions = footprint
      ? { length: footprint.length, width: footprint.width, height: size.y }
      : { length: size.x, width: size.z, height: size.y };
    stopeDimensionsRef.current = dimensions;

    originalRemainingStopeModelRef.current = stope;
    remainingStopeModelRef.current = stope;
    setStopeModel(stope);

    const totalVol = safeCalculateMeshVolume(stope);
    updateStopeFillSceneState({
      volumeData: {
        totalVolume: totalVol,
        remainingVolume: totalVol,
        pours: [],
        dimensions,
      },
      originalStopeModel: stope,
      remainingStopeModel: stope,
    });

    const scaledTopY = (box.max.y + offset.y) * scale + 0.5;
    const scaledCenterX = (center.x + offset.x) * scale;
    const scaledCenterZ = (center.z + offset.z) * scale;
    setArrowPosition(new THREE.Vector3(scaledCenterX, scaledTopY, scaledCenterZ));

    const initialPosition = { x: center.x, y: center.z };
    updateStopeFillSceneState({ pipePosition: initialPosition });

    invalidate();
  }, [loadedStope, loadedGeometryPath]);

  // A failed load must still clear the loading overlay.
  React.useEffect(() => {
    const geometryFailed = !!geometryPath && loadedGeometryPath === geometryPath && !loadedStope;
    if (geometryFailed || poursFailed) {
      updateStopeFillSceneState({ readyGeometryPath: geometryPath });
    }
  }, [geometryPath, loadedGeometryPath, loadedStope, poursFailed]);

  // One-shot camera framing: resets the ortho camera, position, and
  // ArcballControls' internal baselines when the geometry changes. The
  // frustum effect below keeps it in shape across resizes and drawer toggles.
  React.useEffect(() => {
    if (!stopeModel || !threeControls || !camera) return;
    if (framedGeometryPathRef.current === geometryPath) return;
    const orthoCamera = camera as THREE.OrthographicCamera;
    if (!orthoCamera.isOrthographicCamera) return;
    framedGeometryPathRef.current = geometryPath;

    const box = new THREE.Box3().setFromObject(stopeModel);
    const size = box.getSize(new THREE.Vector3());
    const scaledSize = size.clone().multiplyScalar(modelScale);
    const worldRadius = Math.max(scaledSize.x, scaledSize.y, scaledSize.z, 1) / 2;

    const viewSize = worldRadius * 1.5;
    const aspect = canvasSize.width / Math.max(canvasSize.height, 1);
    orthoCamera.left = -viewSize * aspect;
    orthoCamera.right = viewSize * aspect;
    orthoCamera.top = viewSize;
    orthoCamera.bottom = -viewSize;
    orthoCamera.near = -1000000;
    orthoCamera.far = 1000000;
    orthoCamera.zoom = 1;
    baseViewSizeRef.current = viewSize;

    const distance = Math.max(worldRadius * 3, 50);
    orthoCamera.position.set(0, distance * 0.5, distance);
    orthoCamera.up.set(0, 1, 0);
    orthoCamera.lookAt(0, 0, 0);
    orthoCamera.updateProjectionMatrix();
    orthoCamera.updateMatrix();
    orthoCamera.updateMatrixWorld(true);

    const viewWidth = Math.abs(orthoCamera.right - orthoCamera.left);
    const overlayWidth = drawerOpen ? MODEL_DRAW_WIDTH : CLOSED_DRAWER_RAIL_WIDTH;
    const drawerFrustumOffset = ((overlayWidth / 2) * viewWidth) / canvasSize.width;

    focusCameraOnPoint(
      threeControls as any,
      camera,
      new THREE.Vector3(0, 0, 0),
      { radius: worldRadius, padding: 1.5, frustumOffsetX: drawerFrustumOffset },
    );
    invalidate();
  }, [stopeModel, threeControls, camera, geometryPath, modelScale, canvasSize.width, canvasSize.height]);

  // R3F resets an ortho frustum to pixel units on every size or dpr change, so all four sides are re-applied.
  React.useLayoutEffect(() => {
    const viewSize = baseViewSizeRef.current;
    if (framedGeometryPathRef.current !== geometryPath || viewSize === null) return;
    const orthoCamera = camera as THREE.OrthographicCamera;
    if (!orthoCamera?.isOrthographicCamera) return;

    const halfWidth = viewSize * (canvasSize.width / Math.max(canvasSize.height, 1));
    const overlayWidth = drawerOpen ? MODEL_DRAW_WIDTH : CLOSED_DRAWER_RAIL_WIDTH;
    const drawerFrustumOffset = (overlayWidth * halfWidth) / canvasSize.width;
    orthoCamera.left = -halfWidth + drawerFrustumOffset;
    orthoCamera.right = halfWidth + drawerFrustumOffset;
    orthoCamera.top = viewSize;
    orthoCamera.bottom = -viewSize;
    orthoCamera.updateProjectionMatrix();
    invalidate();
  }, [drawerOpen, geometryPath, camera, canvasSize.width, canvasSize.height, viewport.dpr]);

  React.useEffect(() => {
    if (!originalRemainingStopeModelRef.current || !stopeModel) return undefined;
    if (!poursData) return undefined;

    const originalStope = originalRemainingStopeModelRef.current;
    const walkedGeometryPath = stopeModelPathRef.current;
    const totalVolume = safeCalculateMeshVolume(originalStope);
    remainingStopeModelRef.current = originalRemainingStopeModelRef.current;

    const dimensions = stopeDimensionsRef.current ?? undefined;
    const box = stopeBoxRef.current;
    const footprint = stopeFootprintRef.current;
    const annotationsFor = (pourLabels: Array<{ id: string; midY: number; height: number }>): StopeAnnotations | null => (
      box && dimensions && footprint ? buildStopeAnnotations(box, dimensions, footprint, pourLabels) : null
    );

    const showUnfilledStope = () => {
      walkRef.current = null;
      remainingStopeModelRef.current = originalStope;
      setPourModels([]);
      setRemainingStopeModel(originalStope);
      setAnnotations(annotationsFor([]));
      updateStopeFillSceneState({
        volumeData: { totalVolume, remainingVolume: totalVolume, pours: [], dimensions },
        remainingStopeModel: originalStope,
        fillSurfaces: [],
        cumulativeVolume: 0,
        dischargeFloorLevel: 0,
        readyGeometryPath: walkedGeometryPath,
      });
      invalidate();
    };

    if (poursData.length === 0) {
      showUnfilledStope();
      return undefined;
    }

    let cancelled = false;
    // walkStopeFill is the shared stacking kernel (also used by "Update Volumes"): date-ordered cones
    // whose pointwise maximum is the fill surface, with volumes from the exact integral and display
    // shells cut from the pristine stope. Here we drive it for the meshes and the annotation bands.
    const job = walkStopeFillOffThread(originalStope, sortPoursChronologically(poursData), defaultAnchor);

    job.result
      .then(({ walk, steps, unfilledSolid }) => {
        if (cancelled) return;

        // From the integral, not from the CSG shell: the displayed remainder is illustrative, this number
        // is what the engineer plans against.
        const remainingVolume = Math.max(0, walk.totalVolume - walk.cumulativeVolume);

        // The annotation band is the rise AT THE DISCHARGE POINT - from the fill surface under this
        // pour's pipe up to its apex - not the shell's bounding box, which sags at the edges and
        // overstates it. Both bounds are elevations on the discharge axis, so the band is drawable.
        setAnnotations(annotationsFor(steps.map((step) => ({
          id: step.pour.entityId,
          midY: (step.datum + step.surface.level) / 2,
          height: step.riseAtDischarge,
        }))));

        walkRef.current = walk;
        remainingStopeModelRef.current = unfilledSolid;
        setPourModels(steps.map(({ pour, fillSolid }) => ({ entityId: pour.entityId, mesh: fillSolid })));
        setRemainingStopeModel(unfilledSolid);
        updateStopeFillSceneState({
          volumeData: {
            totalVolume: walk.totalVolume,
            remainingVolume,
            pours: steps.map((step): PourVolumeInfo => ({
              entityId: step.pour.entityId,
              name: step.pour.reference || `Pour ${step.index + 1}`,
              volume: step.pour.volume,
              recomputedVolume: step.sliceVolume,
              beachAngleDegrees: step.beachAngleDegrees,
              height: step.riseAtDischarge,
            })),
            dimensions,
          },
          remainingStopeModel: unfilledSolid,
          fillSurfaces: walk.surfaces,
          cumulativeVolume: walk.cumulativeVolume,
          dischargeFloorLevel: walk.dischargeFloorLevel,
          readyGeometryPath: walkedGeometryPath,
        });
        invalidate();
      })
      .catch(() => {
        if (!cancelled) showUnfilledStope();
      });

    return () => {
      cancelled = true;
      job.cancel();
    };
  }, [poursData, stopeModel, defaultAnchor]);

  React.useEffect(() => {
    if (!poursData) return;

    setPoursColor(buildPourColors(poursData, pourColorDisplay));
  }, [poursData, pourColorDisplay]);

  React.useEffect(() => {
    const pristine = originalRemainingStopeModelRef.current;
    if (!previewPour || !pristine) {
      setPreviewPourModel(null);
      setRemainingStopeAfterPreview(null);
      return;
    }

    const anchor: FillAnchor = sceneState.pipePosition
      ? { x: sceneState.pipePosition.x, z: sceneState.pipePosition.y }
      : defaultAnchor;

    // Built on exactly the base walkStopeFill would give this pour when it is committed, and drawn with
    // the same two functions, so the preview IS the pour it becomes.
    // The preview's angle follows the same chain the pour will be created with: the form's override if
    // one is set, otherwise the project default, otherwise the legacy constant.
    const previewAngle = resolveNewPourBeachAngle(previewPour.beachAngleDegrees, projectBeachAngleDegrees);
    const placed = walkRef.current?.surfaces ?? [];
    const base = nextSliceBase(pristine, anchor, previewAngle, placed);
    // Height mode fixes the cone height off the datum; volume mode solves for the level instead.
    const level = previewPour.height != null
      ? base.datum + previewPour.height
      : base.curve.levelAt(base.baseVolume + previewPour.volume);
    const surface: BeachSurface = { level, anchor, slopeDegrees: previewAngle };

    try {
      setPreviewPourModel(buildSliceSolid(pristine, placed, surface) as unknown as ThreeMesh);
      setRemainingStopeAfterPreview(buildUnfilledSolid(
        pristine, [...placed, surface],
      ) as unknown as ThreeMesh);
    } catch {
      setPreviewPourModel(null);
      setRemainingStopeAfterPreview(null);
    }
    invalidate();
  }, [previewPour, defaultAnchor, poursData?.length, sceneState.pipePosition, sceneState.fillSurfaces, projectBeachAngleDegrees]);

  if (!stopeModel) return null;

  const visibleStopeModel = remainingStopeAfterPreview || remainingStopeModel || stopeModel;
  const dimensionSpecs = annotations ? buildDimensionSpecs(annotations) : null;

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 20, 10]} intensity={1.2} />
      <directionalLight position={[-10, 10, -10]} intensity={0.8} />

      <group position={groupPosition} scale={modelScale}>
        <StopeGeometryShell mesh={visibleStopeModel} />

        {pourModels.map(({ entityId, mesh }, index) => {
          const color = poursColor.get(entityId);
          const isSelected = selectedPourId === entityId;

          return (
            <primitive
              key={entityId}
              object={mesh}
              onPointerDown={handlePourPointerDown}
              onClick={(event: { clientX: number; clientY: number }) => handlePourClick(entityId, event)}
            >
              {/* Never conditional: a shell with no material of its own falls back to the material the
                  CSG evaluator handed it, which is the stope's, and the pour vanishes into the stope. */}
              <meshPhongMaterial
                attach="material"
                color={isSelected ? 0xffffff : (color ?? distinctColors[index % distinctColors.length])}
                side={THREE.DoubleSide}
              />
            </primitive>
          );
        })}

        {previewPourModel && (
          <primitive object={previewPourModel}>
            <meshPhongMaterial
              attach="material"
              color={distinctColors[pourModels.length % distinctColors.length]}
              side={THREE.DoubleSide}
            />
          </primitive>
        )}

        {dimensionSpecs && dimensionSpecs.specs.map((spec) => (
          <AxisDimension
            key={spec.key}
            start={spec.start}
            end={spec.end}
            label={spec.label}
            fontSize={dimensionSpecs.fontSize}
            color={spec.color}
          />
        ))}
      </group>

      <group ref={arrowGroupRef} position={arrowPosition}>
        <mesh rotation={[Math.PI, 0, 0]}>
          <coneGeometry args={[0.8, 2, 16]} />
          <meshStandardMaterial color="#ff6600" />
        </mesh>

        <mesh position={[0, 2.5, 0]}>
          <cylinderGeometry args={[0.3, 0.3, 3, 16]} />
          <meshStandardMaterial color="#ff6600" />
        </mesh>
      </group>

      {enablePositionMove && arrowGroupRef.current && (
        <TransformControls
          object={arrowGroupRef.current}
          mode="translate"
          showY={false}
          onObjectChange={handleArrowChange}
        />
      )}
    </>
  );
};

export default StopeFillContent;
